import { Injectable, inject } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    throwError,
    defer,
} from 'rxjs';
import {
    catchError,
    expand,
    finalize,
    map,
    reduce,
    shareReplay,
} from 'rxjs/operators';
import { SEARCH_PAGE_NODE } from '../../../../../graphQL/node-query-strings';

const SEARCH_PAGES_QUERY = gql`
    query SearchPages($first: Int!, $after: String) {
        pages(first: $first, after: $after) {
            pageInfo {
                endCursor
                hasNextPage
            }
            nodes {
                ${SEARCH_PAGE_NODE}
            }
        }
    }
`;

type PagesConnection = {
    pageInfo: { endCursor: string | null; hasNextPage: boolean };
    nodes: any[];
};

@Injectable({
    providedIn: 'root',
})
export class ApolloSearchService {
    private readonly apollo: ApolloBase = inject(Apollo).use('cms');

    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    public readonly loading$ = this.loadingSubject.asObservable();

    private readonly errorSubject = new Subject<unknown>();
    public readonly error$ = this.errorSubject.asObservable();

    fetchSearchPages$(pageSize = 100): Observable<any[]> {
        return defer(() => {
            this.loadingSubject.next(true);

            const fetchPage = (after: string | null) =>
                this.apollo.query<{ pages: PagesConnection }>({
                    query: SEARCH_PAGES_QUERY,
                    variables: { first: pageSize, after },
                });

            return fetchPage(null).pipe(
                map((result) => result.data?.pages),
                expand((conn) =>
                    conn?.pageInfo?.hasNextPage
                        ? fetchPage(conn.pageInfo.endCursor).pipe(
                              map((r) => r.data?.pages),
                          )
                        : EMPTY,
                ),
                map((conn) => conn?.nodes ?? []),
                reduce((all, nodes) => all.concat(nodes), [] as any[]),
                catchError((err) => {
                    this.errorSubject.next(err);
                    return throwError(() => err);
                }),
                finalize(() => this.loadingSubject.next(false)),
            );
        }).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
}
